import { startOfWeek, addWeeks } from 'date-fns';

/**
 * Number of weeks kept mounted in the sliding scheduler window. Parity with the
 * previous MAX_WEEKS=8 DOM ceiling — no memory regression. Exported for tuning.
 */
export const WINDOW_WEEKS = 8;

const mondayOf = (d: Date) => startOfWeek(d, { weekStartsOn: 1 });

/**
 * Build a constant-size array of Monday week-starts centered on `center`'s week:
 * `Math.floor(size/2)` weeks before, the center week, then the remainder after.
 * (size=8 → 4 before, center, 3 after.)
 */
export function buildWindow(center: Date, size: number = WINDOW_WEEKS): Date[] {
  const c = mondayOf(center);
  const halfBefore = Math.floor(size / 2);
  return Array.from({ length: size }, (_, i) => addWeeks(c, i - halfBefore));
}

/** Prepend the week before the first, drop the last. Infinite-scroll into the past. */
export function slideLeft(weeks: Date[]): Date[] {
  if (weeks.length === 0) return weeks;
  return [addWeeks(weeks[0], -1), ...weeks.slice(0, -1)];
}

/** Append the week after the last, drop the first (weeks[0]). Infinite-scroll into the future. */
export function slideRight(weeks: Date[]): Date[] {
  if (weeks.length === 0) return weeks;
  return [...weeks.slice(1), addWeeks(weeks[weeks.length - 1], 1)];
}

/** Rebuild the window centered on an arbitrary target date (Today button, date picker). */
export function recenter(target: Date, size: number = WINDOW_WEEKS): Date[] {
  return buildWindow(target, size);
}

/**
 * Pixel adjustment to keep the viewport stable after a sentinel-triggered slide.
 * `weekPx` = one week's full horizontal advance INCLUDING the inter-column gap
 * (see WEEK_PX in ContractsBoard). A left slide prepends content (everything
 * shifts right) → add; a right slide drops the leftmost week (everything shifts
 * left) → subtract.
 */
export function scrollDeltaForSlide(direction: 'left' | 'right', weekPx: number): number {
  return direction === 'left' ? weekPx : -weekPx;
}
